import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { FileBarChart2, Download, Loader2, CheckCircle2, XCircle, Clock, FileText, Sparkles, RefreshCw, History, ChevronLeft } from 'lucide-react';
import { api } from '../lib/api';

type ReportType = 'WORLD_FINANCIAL_SUMMARY' | 'PLAYER_TRANSACTIONS' | 'P2E_POOL_HEALTH' | 'PLATFORM_OVERVIEW' | 'CB_RESERVE_STATEMENT' | 'ANOMALY_REPORT' | 'FULL_PLATFORM_REPORT' | 'HOLDER_HISTORY';
type Format = 'PDF' | 'CSV' | 'EXCEL';

interface World {
  id: string;
  name: string;
}

interface ReportJob {
  jobId: string;
  reportType: string;
  format: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  filename: string | null;
}

const WORLD_SCOPED_TYPES: ReportType[] = ['WORLD_FINANCIAL_SUMMARY', 'PLAYER_TRANSACTIONS', 'P2E_POOL_HEALTH'];
const HOLDER_SCOPED_TYPES: ReportType[] = ['HOLDER_HISTORY'];

const REPORT_TYPES: { value: ReportType; label: string; desc: string }[] = [
  { value: 'PLATFORM_OVERVIEW',       label: 'Platform Overview',       desc: 'High-level platform metrics' },
  { value: 'CB_RESERVE_STATEMENT',    label: 'CB Reserve Statement',    desc: 'Central bank reserve ledger' },
  { value: 'ANOMALY_REPORT',          label: 'Anomaly Report',          desc: 'Fraud & anomaly flags' },
  { value: 'WORLD_FINANCIAL_SUMMARY', label: 'World Financial Summary', desc: 'Per-world balance sheets — requires a World ID' },
  { value: 'PLAYER_TRANSACTIONS',     label: 'Player Transactions',     desc: 'Full transaction history — requires a World ID' },
  { value: 'P2E_POOL_HEALTH',         label: 'P2E Pool Health',         desc: 'Reward pool analytics — requires a World ID' },
  { value: 'FULL_PLATFORM_REPORT',    label: 'Full Platform Report',    desc: 'All reports in one — platform + every active world' },
  { value: 'HOLDER_HISTORY',          label: 'Holder History',          desc: 'Complete balance & transaction history for a holder — requires a Holder ID' },
];

const FORMAT_LABELS: Record<Format, string> = { PDF: 'PDF', CSV: 'CSV', EXCEL: 'Excel' };

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';

/** Authenticated download — uses axios so the JWT is attached, avoids 401 from plain <a href> */
async function downloadReport(jobId: string, filename: string) {
  const resp = await api.get(`/reports/${jobId}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function JobStatusCard({ jobId }: { jobId: string }) {
  const { data: job } = useQuery({
    queryKey: ['report-job', jobId],
    queryFn: () => api.get(`/reports/${jobId}`).then((r) => r.data),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = (query.state.data as { status?: string } | undefined)?.status;
      return status === 'PENDING' || status === 'GENERATING' ? 3000 : false;
    },
  });

  const status = (job as { status?: string } | undefined)?.status ?? 'CHECKING';
  const isDone = status === 'READY';
  const isFailed = status === 'FAILED';
  const isPending = !isDone && !isFailed;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5"
      style={{ borderColor: isDone ? 'rgba(16,185,129,0.25)' : isFailed ? 'rgba(239,68,68,0.25)' : 'rgba(0,212,255,0.15)' }}
    >
      <div className="flex items-center gap-3 mb-3">
        {isDone && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
        {isFailed && <XCircle className="w-5 h-5 text-red-400" />}
        {isPending && <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />}
        <h3 className="text-sm font-semibold text-white">Report Job</h3>
        <span className={`ml-auto text-xs px-2.5 py-1 rounded-full font-medium ${
          isDone ? 'badge-active' : isFailed ? 'badge-critical' : 'badge-warning'
        }`}>
          {status}
        </span>
      </div>
      <p className="font-mono text-xs text-slate-600 mb-3">{jobId}</p>
      {isDone && (job as { filename?: string } | undefined)?.filename && (
        <button
          onClick={() => downloadReport(jobId, (job as { filename: string }).filename)}
          className="inline-flex items-center gap-2 btn-primary text-xs font-semibold px-4 py-2 rounded-xl"
        >
          <Download className="w-3.5 h-3.5" />
          Download {(job as { filename?: string }).filename}
        </button>
      )}
      {isFailed && <p className="text-xs text-red-400">Report generation failed. Please try again.</p>}
      {isPending && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Clock className="w-3.5 h-3.5" />
          Processing... auto-refreshing every 3s
        </div>
      )}
    </motion.div>
  );
}

export function ReportsPage() {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState<ReportType>('PLATFORM_OVERVIEW');
  const [format, setFormat] = useState<Format>('PDF');
  const [from, setFrom] = useState(() => new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [worldId, setWorldId] = useState('');
  const [holderId, setHolderId] = useState('');
  const [jobId, setJobId] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: history, isLoading: historyLoading } = useQuery<ReportJob[]>({
    queryKey: ['report-history'],
    queryFn: () => api.get('/reports').then((r) => r.data),
    refetchInterval: 10_000,
  });

  const submitMutation = useMutation({
    mutationFn: (data: object) => api.post('/reports', data).then((r) => r.data),
    onSuccess: (data) => {
      setJobId((data as { jobId: string }).jobId);
      void queryClient.invalidateQueries({ queryKey: ['report-history'] });
    },
  });

  const requiresWorldId  = WORLD_SCOPED_TYPES.includes(reportType);
  const requiresHolderId = HOLDER_SCOPED_TYPES.includes(reportType);

  const { data: worlds = [], isLoading: worldsLoading } = useQuery<World[]>({
    queryKey: ['worlds-list'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
    staleTime: 60_000,
    enabled: requiresWorldId,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (requiresWorldId  && !worldId.trim())  return;
    if (requiresHolderId && !holderId.trim()) return;
    submitMutation.mutate({
      reportType,
      format,
      from,
      to,
      worldId:  worldId.trim()  || undefined,
      holderId: holderId.trim() || undefined,
    });
  };

  const handleDownload = async (job: ReportJob) => {
    if (!job.filename) return;
    setDownloading(job.jobId);
    try {
      await downloadReport(job.jobId, job.filename);
    } finally {
      setDownloading(null);
    }
  };

  const selectedType = REPORT_TYPES.find((r) => r.value === reportType);
  const readyJobs = (history ?? []).filter((j) => j.status === 'READY');
  const pendingJobs = (history ?? []).filter((j) => j.status === 'PENDING' || j.status === 'GENERATING');
  const failedJobs = (history ?? []).filter((j) => j.status === 'FAILED');

  return (
    <div className="p-8 space-y-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 mb-3 transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back
        </button>
        <h2 className="text-xl font-bold text-white">Reports</h2>
        <p className="text-sm text-slate-600 mt-0.5">Generate and export financial reports</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Form panel */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="xl:col-span-2 glass-card p-6"
        >
          <div className="flex items-center gap-2 mb-6">
            <FileBarChart2 className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-semibold text-white">Request a Report</h3>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-3">Report Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {REPORT_TYPES.map((rt) => (
                  <button
                    key={rt.value}
                    type="button"
                    onClick={() => {
                      setReportType(rt.value);
                      if (!WORLD_SCOPED_TYPES.includes(rt.value)) setWorldId('');
                      if (!HOLDER_SCOPED_TYPES.includes(rt.value)) setHolderId('');
                    }}
                    className={`text-left px-4 py-3 rounded-xl border transition-all ${
                      reportType === rt.value
                        ? 'border-cyan-500/40 bg-cyan-500/10 text-white'
                        : 'border-white/5 bg-white/[0.02] text-slate-400 hover:border-white/10 hover:text-slate-300'
                    }`}
                  >
                    <div className="text-xs font-semibold">{rt.label}</div>
                    <div className="text-[10px] text-slate-600 mt-0.5">{rt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2">Format</label>
                <div className="flex gap-2">
                  {(['PDF', 'CSV', 'EXCEL'] as Format[]).map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFormat(f)}
                      className={`flex-1 text-xs py-2 rounded-xl border transition-all font-medium ${
                        format === f
                          ? 'border-purple-500/40 bg-purple-500/10 text-purple-300'
                          : 'border-white/5 bg-white/[0.02] text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {FORMAT_LABELS[f]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2">From</label>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2">To</label>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
              </div>
            </div>

{requiresHolderId && (
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium mb-2"
                     style={{ color: !holderId ? 'rgb(239 68 68 / 0.8)' : 'rgb(100 116 139)' }}>
                Holder ID
                <span className="text-red-400">* required for this report type</span>
              </label>
              <input
                type="text"
                value={holderId}
                onChange={(e) => setHolderId(e.target.value)}
                placeholder="Holder UUID (e.g. 3fa85f64-5717-...)"
                className={`${inputCls}${!holderId ? ' border-red-500/30' : ''}`}
              />
            </div>
          )}

          {requiresWorldId && (
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium mb-2"
                     style={{ color: !worldId ? 'rgb(239 68 68 / 0.8)' : 'rgb(100 116 139)' }}>
                World
                <span className="text-red-400">* required for this report type</span>
              </label>
              {worldsLoading ? (
                <div className={`${inputCls} flex items-center gap-2 text-slate-600`}>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading worlds...
                </div>
              ) : (
                <select
                  value={worldId}
                  onChange={(e) => setWorldId(e.target.value)}
                  required
                  className={`${inputCls} cursor-pointer${!worldId ? ' border-red-500/30' : ''}`}
                >
                  <option value="">— Select a world —</option>
                  {worlds.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

            <button
              type="submit"
              disabled={submitMutation.isPending || (requiresWorldId && !worldId.trim()) || (requiresHolderId && !holderId.trim())}
              className="w-full btn-primary flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitMutation.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Queuing...</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate Report</>
              )}
            </button>

            {submitMutation.isError && (
              <p className="text-red-400 text-xs text-center">Failed to queue report. Please try again.</p>
            )}
          </form>
        </motion.div>

        {/* Preview / status panel */}
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.35 }}
            className="glass-card p-5"
          >
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-white">Report Preview</h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">Type</p>
                <p className="text-sm font-semibold text-white">{selectedType?.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{selectedType?.desc}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">Format</p>
                <p className="text-sm text-white">{FORMAT_LABELS[format]}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">Date Range</p>
                <p className="text-xs text-slate-400">{from} &rarr; {to}</p>
              </div>
              {worldId && (
                <div>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">World Filter</p>
                  <p className="font-mono text-xs text-cyan-400">{worldId}</p>
                </div>
              )}
              {holderId && (
                <div>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">Holder ID</p>
                  <p className="font-mono text-xs text-purple-400">{holderId}</p>
                </div>
              )}
            </div>
          </motion.div>

          <AnimatePresence>
            {jobId && <JobStatusCard jobId={jobId} />}
          </AnimatePresence>
        </div>
      </div>

      {/* Generated Reports History */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.35 }}
        className="glass-card p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-white">Generated Reports</h3>
            {pendingJobs.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full badge-warning font-medium">
                {pendingJobs.length} processing
              </span>
            )}
          </div>
          <button
            onClick={() => void queryClient.invalidateQueries({ queryKey: ['report-history'] })}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>

        {historyLoading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reports...
          </div>
        )}

        {!historyLoading && (!history || history.length === 0) && (
          <p className="text-sm text-slate-600">No reports generated yet. Generate your first report above.</p>
        )}

        {!historyLoading && history && history.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-slate-500 font-medium pb-2 pr-4">Report Type</th>
                  <th className="text-left text-slate-500 font-medium pb-2 pr-4">Format</th>
                  <th className="text-left text-slate-500 font-medium pb-2 pr-4">Status</th>
                  <th className="text-left text-slate-500 font-medium pb-2 pr-4">Requested</th>
                  <th className="text-left text-slate-500 font-medium pb-2 pr-4">Completed</th>
                  <th className="text-right text-slate-500 font-medium pb-2">Download</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.03]">
                {history.map((job) => (
                  <tr key={job.jobId} className="group hover:bg-white/[0.02] transition-colors">
                    <td className="py-2.5 pr-4 text-white font-medium">
                      {REPORT_TYPES.find((r) => r.value === job.reportType)?.label ?? job.reportType}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-400">{job.format}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`px-2 py-0.5 rounded-full font-medium ${
                        job.status === 'READY'      ? 'badge-active' :
                        job.status === 'FAILED'     ? 'badge-critical' :
                        job.status === 'GENERATING' ? 'text-cyan-400 bg-cyan-400/10' :
                                                      'badge-warning'
                      }`}>
                        {job.status === 'GENERATING' ? (
                          <span className="inline-flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" />{job.status}</span>
                        ) : job.status}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-slate-500">
                      {new Date(job.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-500">
                      {job.completedAt ? new Date(job.completedAt).toLocaleString() : '—'}
                    </td>
                    <td className="py-2.5 text-right">
                      {job.status === 'READY' && job.filename ? (
                        <button
                          onClick={() => void handleDownload(job)}
                          disabled={downloading === job.jobId}
                          className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 font-medium transition-colors disabled:opacity-50"
                        >
                          {downloading === job.jobId ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          {job.filename.split('_')[0]}
                        </button>
                      ) : job.status === 'FAILED' ? (
                        <span className="text-red-400 text-xs" title={job.errorMessage ?? ''}>Failed</span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}